import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, BookOpen, Clock, Check, ArrowRight, Zap, Bell, Smartphone, Compass, HandHeart, Home, Baby, Mountain, UserRound } from 'lucide-react';
import SEO from '../components/SEO';
import LandingNavbar from '../components/landing/LandingNavbar';
import LandingFooter from '../components/landing/LandingFooter';
import { PrayersSlimBand } from '../components/funnel/PrayersPlacements';
import { ExitIntentPrayers } from '../components/funnel/ExitIntentPrayers';
import { GrowthAwareSocialProof } from '../components/landing/GrowthAwareSocialProof';
import { RealTestimonials } from '../components/landing/RealTestimonials';
import { ChurchTestimonialWidget } from '../components/landing/ChurchTestimonialWidget';
import { StickyCTABar } from '../components/landing/StickyCTABar';
import { RecoveryToServicePromoBanner } from '../components/landing/RecoveryToServicePromoBanner';
import { CountryFlagsBanner } from '../components/CountryFlagsBanner';
import { SpanishAvailableBadge } from '../components/SpanishAvailableBadge';
import { QuizPromptBanner } from '../components/QuizPromptBanner';
import { WhyCFRBand } from '../components/WhyCFRBand';
import { useCurrency } from '../context/CurrencyContext';
import { 
  TrustBadges 
} from '../components/ConversionOptimizations';
import {
  trackHeroSignupClick,
  trackHeroCoursesClick,
  trackAICompanionCTAClick,
  trackViewAllCoursesClick,
  trackFinalCTAClick,
  trackCourseCardClick
} from '../utils/conversionTracking';

// Design system from design_guidelines.json
const COLORS = {
  primary: '#1E3A8A',
  secondary: '#789B73',
  accent: '#B5533C',
  background: '#FDFBF7',
  surface: '#FFFFFF',
  navy: '#1E3A8A',
  sage: '#789B73',
  sageDeep: '#5F7057',
  rust: '#B5533C',
  cream: '#FDFBF7',
  white: '#FFFFFF'
};

const NewLandingPageClean = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { formatPrice, formatPrice99 } = useCurrency();

  useEffect(() => {
    i18n.changeLanguage(location.pathname.startsWith('/es') ? 'es' : 'en');
  }, [location.pathname, i18n]);

  // Rotating testimonials for hero section
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  
  const heroTestimonials = [
    {
      text: "CFR saved my life when 5 rehabs couldn't.",
      author: "Michael R.",
      role: "3 years sober"
    },
    {
      text: "Other programs separated us. CFR brought our family back together.",
      author: "Sarah K.",
      role: "Nairobi, Kenya · Family member"
    },
    {
      text: "The courses didn't just tell me to stop — they rooted my recovery in the gospel.",
      author: "Mark",
      role: "2 years in recovery"
    },
    {
      text: "I tried everything. CFR was the first thing that actually worked.",
      author: "Jennifer M.",
      role: "2 years in recovery"
    },
    {
      text: "Finally, a program that doesn't make me feel alone in this.",
      author: "Tom H.",
      role: "Lagos, Nigeria · Addict in recovery"
    },
    {
      text: "CFR gave me hope when I had none left.",
      author: "Rebecca S.",
      role: "Supporting my husband"
    }
  ];
  
  useEffect(() => {
    if (window.location.hash === '#testimonials') {
      setTimeout(() => document.getElementById('testimonials')?.scrollIntoView({ behavior: 'smooth' }), 600);
    }
  }, []);

  useEffect(() => {
    // Rotate testimonials every 5 seconds
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % heroTestimonials.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <SEO 
        title="Christian Family Recovery | Unified Recovery Support"
        description="The only recovery platform designed for addicts AND their families to heal together. 9 faith-based courses, real community and a grace-filled path — with your first course completely free, forever."
        keywords="christian addiction recovery, family recovery support, faith-based recovery courses, unified recovery, Walk It Out program"
        lang={location.pathname.startsWith('/es') ? 'es' : 'en'}
        canonicalUrl={location.pathname.startsWith('/es') ? "https://www.christianfamilyrecovery.com/es" : "https://www.christianfamilyrecovery.com/"}
        alternateEnUrl="https://www.christianfamilyrecovery.com/"
        alternateEsUrl="https://www.christianfamilyrecovery.com/es"
      />

      <div style={{ background: COLORS.cream, minHeight: '100vh' }}>
        <LandingNavbar 
          mobileMenuOpen={mobileMenuOpen} 
          setMobileMenuOpen={setMobileMenuOpen} 
          navigate={navigate} 
        />
        <StickyCTABar />

        {/* Mobile Spacing Styles */}
        <style>
          {`
            @media (max-width: 768px) {
              .hero-mobile-spacing {
                padding: 5rem 0 3rem !important;
              }
              .hero-mobile-spacing .container {
                padding-left: 1.5rem !important;
                padding-right: 1.5rem !important;
              }
              .hero-mobile-spacing h1 {
                margin-bottom: 1.5rem !important;
                line-height: 1.2 !important;
              }
              .hero-mobile-spacing p {
                margin-bottom: 2rem !important;
              }
              .mobile-section-spacing {
                padding: 3rem 0 !important;
              }
              
              /* Center all content on mobile */
              .mobile-section-spacing h2,
              .mobile-section-spacing h3,
              .mobile-section-spacing p {
                text-align: center !important;
              }
              
              /* Center buttons and CTAs */
              .mobile-section-spacing button {
                margin-left: auto !important;
                margin-right: auto !important;
              }
              
              /* Center course cards content */
              .mobile-section-spacing [data-testid*="course"] > div {
                text-align: center !important;
              }
            }
          `}
        </style>

        {/* Hero Section */}
        <section 
          style={{ 
            position: 'relative',
            padding: '6rem 0 4rem',
            background: `linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.sage} 100%)`,
            overflow: 'hidden'
          }}
          className="hero-mobile-spacing"
        >
          {/* Decorative overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url('https://images.unsplash.com/photo-1505189014261-0148f9aefa85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBodWdnaW5nJTIwd2FybXxlbnwwfHx8fDE3ODgxMjUxNDl8MA&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15
          }} />

          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2 }}
              style={{ maxWidth: '1140px', margin: '0 auto', textAlign: 'center' }}
            >
              <div style={{
                display: 'inline-block',
                background: COLORS.accent,
                color: COLORS.white,
                padding: '0.5rem 1.5rem',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '1.5rem',
                letterSpacing: '0.05em'
              }}>
                {(() => {
                  const badge = t('home.badge');
                  return badge.startsWith('THE ')
                    ? <><u data-testid="hero-badge-the" style={{ textDecorationThickness: '2px', textUnderlineOffset: '3px' }}>THE</u>{badge.slice(3)}</>
                    : badge;
                })()}
              </div>
              
              <h1 style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
                fontWeight: 800,
                color: COLORS.white,
                marginBottom: '1.5rem',
                lineHeight: 1.1,
                letterSpacing: '-0.02em'
              }}>
                {t('home.heroTitleLine1')}<br /><em>{t('home.heroTitleLine2')}</em>
              </h1>

              <p data-testid="hero-subline" style={{
                fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
                color: 'rgba(255,255,255,0.95)',
                marginBottom: '2rem',
                lineHeight: 1.6,
                maxWidth: 760,
                margin: '0 auto 2rem'
              }}>
                {t('home.heroSubline').split(/<b>|<\/b>/).map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part)}
              </p>

              <p data-testid="hero-ministry-statement" style={{
                fontSize: 'clamp(0.9375rem, 2vw, 1.0625rem)',
                color: 'rgba(255,255,255,0.8)',
                fontStyle: 'italic',
                lineHeight: 1.7,
                maxWidth: 700,
                margin: '0 auto 2rem'
              }}>
                {t('home.ministryStatement')}
              </p>

              <div style={{ 
                display: 'flex', 
                gap: '1rem', 
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => {
                    trackHeroSignupClick();
                    navigate('/signup');
                  }}
                  data-testid="hero-signup-cta"
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    color: COLORS.navy,
                    padding: '1.25rem 3rem',
                    borderRadius: '9999px',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 10px 30px rgba(245, 158, 11, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 15px 40px rgba(245, 158, 11, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(245, 158, 11, 0.4)';
                  }}
                >
                  {t('home.ctaSignup')}
                  <ArrowRight size={20} />
                </button>

                <button
                  onClick={() => {
                    trackHeroCoursesClick();
                    navigate('/buy-courses');
                  }}
                  data-testid="hero-courses-cta"
                  style={{
                    background: COLORS.sageDeep,
                    color: COLORS.white,
                    padding: '1.25rem 3rem',
                    borderRadius: '9999px',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.6rem',
                    boxShadow: '0 10px 30px rgba(120, 155, 115, 0.4)',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 15px 40px rgba(120, 155, 115, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(120, 155, 115, 0.4)';
                  }}
                >
                  <BookOpen size={20} />
                  {t('home.ctaCourses')}
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.75rem', alignItems: 'center', marginTop: '1.5rem' }}>
                <a href="/quiz" data-testid="hero-quiz-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.85)', fontWeight: 600, textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.4)', textUnderlineOffset: '4px', fontSize: '0.9375rem' }}>
                  <Compass size={16} />
                  {t('home.quizLink')}
                </a>
                <a href="/courses" data-testid="hero-what-we-offer-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.85)', fontWeight: 600, textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.4)', textUnderlineOffset: '4px', fontSize: '0.9375rem' }}>
                  See everything we offer →
                </a>
              </div>

              <div style={{ marginTop: '2rem' }}>
                <GrowthAwareSocialProof colors={COLORS} />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Why CFR — the four-people positioning, placed directly under the hero */}
        <WhyCFRBand inline />

        {/* Family Lineup — the "aha" grid: everyone at the table finds their seat */}
        <section style={{ padding: '5rem 0', background: COLORS.cream }} className="mobile-section-spacing">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1 }}
              style={{ textAlign: 'center', marginBottom: '3rem' }}
            >
              <h2 data-testid="family-lineup-headline" style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 700,
                color: COLORS.navy,
                marginBottom: '1rem'
              }}>
                {t('home.lineupTitle1')}
              </h2>
              <p style={{
                fontSize: '1.25rem',
                color: '#4B5563',
                maxWidth: '650px',
                margin: '0 auto'
              }}>
                {t('home.lineupTitle2')}
              </p>
            </motion.div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(190px, 100%), 1fr))',
              gap: '1.25rem',
              maxWidth: '1200px',
              margin: '0 auto 3rem'
            }}>
              {[
                { icon: UserRound, role: 'The Addict', course: 'Walking It Out', meta: '8 weeks', color: COLORS.sage, path: '/walking-it-out' },
                { icon: HandHeart, role: 'Spouse of the Addict', course: 'Still Standing', meta: '12 weeks', color: COLORS.rust, path: '/still-standing' },
                { icon: Home, role: 'Parent of the Addict', course: 'Still Watching the Road', meta: '12 weeks', color: COLORS.navy, path: '/still-watching-the-road' },
                { icon: Baby, role: 'Children of the Addict, 4–7', course: 'Little Hearts', meta: '8 weeks', color: '#B7862C', path: '/little-hearts' },
                { icon: Baby, role: 'Children of the Addict, 8–12', course: 'Growing Hearts', meta: '8 weeks', color: '#B7862C', path: '/growing-hearts' },
                { icon: Baby, role: 'Children of the Addict, 13–17', course: 'Young Hearts', meta: '8 weeks', color: '#B7862C', path: '/young-hearts' },
                { icon: Mountain, role: 'Adult Child of the Addict', course: 'The Weight You Grew Up With', meta: '12 weeks', color: '#3C5F5C', path: '/the-weight-you-grew-up-with' },
              ].map((card, i) => (
                <motion.div
                  key={card.role}
                  data-testid={`family-lineup-card-${i}`}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(card.path)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(card.path); }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -6, boxShadow: '0 12px 28px rgba(0,0,0,0.12)' }}
                  whileTap={{ scale: 0.97, boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  style={{
                    background: COLORS.white,
                    borderRadius: '14px',
                    padding: '1.5rem 1.25rem',
                    textAlign: 'center',
                    border: `2px solid ${card.color}33`,
                    borderTop: `4px solid ${card.color}`,
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%', background: `${card.color}1A`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.85rem'
                  }}>
                    <card.icon size={22} style={{ color: card.color }} />
                  </div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.navy, marginBottom: '0.35rem' }}>{card.role}</div>
                  <div style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '0.35rem' }}>{card.course}</div>
                  <div style={{ fontSize: '0.75rem', color: card.color, fontWeight: 600, marginBottom: '0.5rem' }}>{card.meta}</div>
                  <SpanishAvailableBadge slug={card.path.replace('/', '')} style={{ fontSize: '0.65rem', padding: '0.15rem 0.5rem', marginBottom: '0.6rem' }} />
                  <div style={{ fontSize: '0.8rem', color: card.color, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    {t('home.viewCourse')} <ArrowRight size={13} />
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              style={{ textAlign: 'center' }}
            >
              <p style={{ fontSize: '1rem', color: '#4B5563', marginBottom: '1.25rem' }}>
                Everyone here gets their <strong>own private account</strong> — your first course is always free, forever. {formatPrice(5)}/month unlocks two courses at once, or {formatPrice99(9.99)}/month for every course. Nobody shares a login or a chat history.
              </p>
            </motion.div>
          </div>
        </section>

        <QuizPromptBanner testId="family-lineup-quiz-banner" style={{ maxWidth: '900px' }} />

        {/* ImpactStrip now rendered inside GrowthAwareSocialProof once real growth crosses the honesty threshold */}


        {/* Unified "Who Is This For" Section with Funnel CTAs */}
        <section style={{ padding: '6rem 0', background: COLORS.white }} className="mobile-section-spacing">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.2 }}
              style={{ textAlign: 'center', marginBottom: '4rem' }}
            >
              <h2 style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 700,
                color: COLORS.navy,
                marginBottom: '1rem'
              }}>
                Choose Your Path to Recovery
              </h2>
              <p style={{
                fontSize: '1.25rem',
                color: COLORS.text?.muted || '#4B5563',
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                Everyone's journey is different. Select the path that fits you best — your first course is completely free.
              </p>
            </motion.div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
              gap: '2rem',
              maxWidth: '1200px',
              margin: '0 auto'
            }}>
              {/* For Individuals Struggling */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                style={{
                  background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
                  borderRadius: '16px',
                  padding: '2.5rem',
                  border: '2px solid #BAE6FD',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onClick={() => navigate('/signup/addict')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(120, 155, 115, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💚</div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: COLORS.navy,
                  marginBottom: '1rem'
                }}>
                  I'm Struggling with Addiction
                </h3>
                <p style={{
                  fontSize: '1rem',
                  color: '#475569',
                  marginBottom: '1.5rem',
                  lineHeight: 1.6
                }}>
                  24/7 AI support, proven 8-week course, and a community that understands. Start your recovery journey today.
                </p>
                <button
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #789B73 0%, #5A7A5A 100%)',
                    color: 'white',
                    padding: '1rem 2rem',
                    borderRadius: '9999px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  Start My Journey <ArrowRight size={18} />
                </button>
              </motion.div>

              {/* For Family Members */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                style={{
                  background: 'linear-gradient(135deg, #FFF5F5 0%, #FFF1F2 100%)',
                  borderRadius: '16px',
                  padding: '2.5rem',
                  border: '2px solid #FECDD3',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onClick={() => navigate('/signup/family')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(200, 106, 83, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💔</div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: COLORS.navy,
                  marginBottom: '1rem'
                }}>
                  I'm Supporting a Loved One
                </h3>
                <p style={{
                  fontSize: '1rem',
                  color: '#475569',
                  marginBottom: '1.5rem',
                  lineHeight: 1.6
                }}>
                  Learn healthy boundaries, find peace, and heal alongside your loved one. Walk the same path together.
                </p>
                <button
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #C86A53 0%, #A8553F 100%)',
                    color: 'white',
                    padding: '1rem 2rem',
                    borderRadius: '9999px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  Get Support <ArrowRight size={18} />
                </button>
              </motion.div>

              {/* For Church Leaders */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                style={{
                  background: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)',
                  borderRadius: '16px',
                  padding: '2.5rem',
                  border: '2px solid #DDD6FE',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
                onClick={() => navigate('/signup/church')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 60px rgba(124, 58, 237, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⛪</div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: COLORS.navy,
                  marginBottom: '1rem'
                }}>
                  I'm a Church Leader
                </h3>
                <p style={{
                  fontSize: '1rem',
                  color: '#475569',
                  marginBottom: '1.5rem',
                  lineHeight: 1.6
                }}>
                  Equip your congregation with 24/7 recovery support. No training required. Bulk licensing available.
                </p>
                <button
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                    color: 'white',
                    padding: '1rem 2rem',
                    borderRadius: '9999px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  Explore Partnership <ArrowRight size={18} />
                </button>
              </motion.div>
            </div>
          </div>
        </section>


        {/* Course Preview & Trust */}
        <section style={{ padding: '6rem 0', background: COLORS.white }} className="mobile-section-spacing">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.2 }}
              style={{ textAlign: 'center', marginBottom: '3rem' }}
            >
              <h2 style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 800,
                color: COLORS.navy,
                marginBottom: '1rem'
              }}>
                Hear a Course For Yourself
              </h2>
              <p style={{
                fontSize: '1.25rem',
                color: COLORS.text?.muted || '#4B5563',
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: 1.6
              }}>
                A real devotional from inside our courses — this is what your quiz-matched free course actually sounds like.
              </p>
            </motion.div>


            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.2, delay: 0.2 }}
              data-testid="member-companion-note"
              style={{
                maxWidth: '760px',
                margin: '2.5rem auto 0',
                background: COLORS.cream,
                padding: '1.5rem 2rem',
                borderRadius: '1.25rem',
                borderLeft: `4px solid ${COLORS.sage}`,
                textAlign: 'center'
              }}
            >
              <p style={{ fontSize: '1rem', lineHeight: 1.7, color: COLORS.text?.muted || '#4B5563', margin: 0 }}>
                Every member also gets a private, faith-trained chat companion inside the course area for support between lessons — never a replacement for real community, just one more way we walk with you.
              </p>
            </motion.div>

            {/* Trust Badges */}
            <div style={{ marginTop: '2.5rem' }}>
              <TrustBadges />
            </div>
          </div>
        </section>


        <PrayersSlimBand source="homepage_band" />

        {/* Streamlined Testimonials */}
        <section id="testimonials" style={{ padding: '6rem 0', background: COLORS.cream, scrollMarginTop: '90px' }} className="mobile-section-spacing">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.2 }}
              style={{ textAlign: 'center', marginBottom: '4rem' }}
            >
              <h2 style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 700,
                color: COLORS.navy,
                marginBottom: '1rem'
              }}>
                Real Stories. Real Hope.
              </h2>
            </motion.div>
            <RealTestimonials colors={COLORS} />
            <ChurchTestimonialWidget colors={COLORS} />
          </div>
        </section>


        <RecoveryToServicePromoBanner />

        <CountryFlagsBanner
          sectionTestId="homepage-countries-banner"
          flagTestIdPrefix="homepage-flag"
          bg="#233240"
          caption="Peer-to-peer support and organisation standards you can trust — for candidates and sponsoring organisations across:"
        />

        <section data-testid="homepage-badge-teaser" style={{ background: '#1B2732', padding: '1.5rem 1.5rem 2.5rem', textAlign: 'center' }}>
          <button
            data-testid="homepage-badge-teaser-btn"
            onClick={() => navigate('/recovery-to-service/verify')}
            style={{
              background: 'transparent', border: `1.5px solid ${COLORS.rust}`, color: '#F4F2EC',
              padding: '0.65rem 1.5rem', borderRadius: '999px', fontWeight: 700, fontSize: '0.85rem',
              cursor: 'pointer', letterSpacing: '0.02em', display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            }}
          >
            See who's registered — verify a peer supporter's qualification →
          </button>
        </section>

        {/* Final CTA */}
        <section style={{
          padding: '6rem 0',
          background: `linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.sage} 100%)`,
          color: COLORS.white
        }} className="mobile-section-spacing">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.2 }}
              style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}
            >
              <h2 style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 700,
                marginBottom: '1.5rem',
                color: COLORS.white
              }}>
                You Don't Walk This Alone
              </h2>
              <p style={{
                fontSize: '1.25rem',
                marginBottom: '2.5rem',
                opacity: 0.95,
                lineHeight: 1.6
              }}>
                Join thousands of families healing together. 1 free course, picked by your quiz recommendation. No credit card required.
              </p>
              <button
                onClick={() => {
                  trackFinalCTAClick();
                  navigate('/signup');
                }}
                data-testid="final-signup-cta"
                style={{
                  background: COLORS.white,
                  color: COLORS.navy,
                  padding: '1.25rem 3.5rem',
                  borderRadius: '9999px',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  width: 'auto',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Start Your Journey Today
                <ArrowRight size={24} />
              </button>
            </motion.div>
          </div>
        </section>

        <LandingFooter />

        <ExitIntentPrayers />
      </div>
    </>
  );
};

export default NewLandingPageClean;
